import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { staffApi } from '@/lib/api';
import { useAuthStore } from '@/hooks/useAuthStore';

interface StaffMember {
  id: string;
  email: string;
  nombre: string;
  role: string;
  activo: boolean;
  mfaEnabled: boolean;
  mfaVerified: boolean;
  lastLoginAt: string | null;
  failedLogins: number;
  lockedUntil: string | null;
  createdAt: string;
}

const ROLES = ['SUPER_ADMIN', 'ADMIN', 'SUPPORT', 'READ_ONLY'];

const roleColors: Record<string, string> = {
  SUPER_ADMIN: 'bg-purple-100 text-purple-800',
  ADMIN: 'bg-blue-100 text-blue-800',
  SUPPORT: 'bg-green-100 text-green-800',
  READ_ONLY: 'bg-gray-100 text-gray-800',
};

export default function Staff() {
  const queryClient = useQueryClient();
  const { staff: currentUser } = useAuthStore();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<StaffMember | null>(null);
  const [search, setSearch] = useState('');

  // Fetch staff list
  const { data, isLoading, error } = useQuery({
    queryKey: ['staff', search],
    queryFn: () => staffApi.getAll({ search: search || undefined, limit: 100 }),
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: staffApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      setShowCreateModal(false);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { nombre?: string; role?: string; activo?: boolean } }) =>
      staffApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      setSelectedStaff(null);
    },
  });

  // Unlock mutation
  const unlockMutation = useMutation({
    mutationFn: staffApi.unlock,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['staff'] }),
  });

  // Disable MFA mutation
  const disableMfaMutation = useMutation({
    mutationFn: staffApi.disableMfa,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['staff'] }),
  });

  // Deactivate mutation
  const deactivateMutation = useMutation({
    mutationFn: staffApi.deactivate,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['staff'] }),
  });

  const isSuperAdmin = currentUser?.role === 'SUPER_ADMIN';

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Staff Management</h1>
          <p className="text-gray-600">Gestiona usuarios administrativos</p>
        </div>
        {isSuperAdmin && (
          <button onClick={() => setShowCreateModal(true)} className="btn-primary">
            + Nuevo Staff
          </button>
        )}
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Buscar por email o nombre..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input max-w-md"
        />
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          Error al cargar staff: {(error as Error).message}
        </div>
      )}

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                  Usuario
                </th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                  Rol
                </th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                  Estado
                </th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                  MFA
                </th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                  Ultimo Login
                </th>
                <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data?.data?.staff?.map((member: StaffMember) => (
                <tr key={member.id} className="hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <div>
                      <div className="font-medium text-gray-900">{member.nombre}</div>
                      <div className="text-sm text-gray-500">{member.email}</div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleColors[member.role]}`}>
                      {member.role}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    {member.lockedUntil && new Date(member.lockedUntil) > new Date() ? (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Bloqueado
                      </span>
                    ) : member.activo ? (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Activo
                      </span>
                    ) : (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Inactivo
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    {member.mfaEnabled && member.mfaVerified ? (
                      <span className="text-green-600">✓ Activo</span>
                    ) : member.mfaEnabled ? (
                      <span className="text-yellow-600">Pendiente</span>
                    ) : (
                      <span className="text-gray-400">No</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-500">
                    {member.lastLoginAt
                      ? new Date(member.lastLoginAt).toLocaleDateString('es-MX')
                      : 'Nunca'}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex justify-end gap-2">
                      {isSuperAdmin && (
                        <button
                          onClick={() => setSelectedStaff(member)}
                          className="text-sm text-primary-600 hover:text-primary-700"
                        >
                          Editar
                        </button>
                      )}
                      {member.lockedUntil && new Date(member.lockedUntil) > new Date() && (
                        <button
                          onClick={() => unlockMutation.mutate(member.id)}
                          className="text-sm text-green-600 hover:text-green-700"
                          disabled={unlockMutation.isPending}
                        >
                          Desbloquear
                        </button>
                      )}
                      {isSuperAdmin && member.mfaEnabled && (
                        <button
                          onClick={() => {
                            if (confirm('¿Seguro que deseas deshabilitar MFA?')) {
                              disableMfaMutation.mutate(member.id);
                            }
                          }}
                          className="text-sm text-yellow-600 hover:text-yellow-700"
                          disabled={disableMfaMutation.isPending}
                        >
                          Quitar MFA
                        </button>
                      )}
                      {isSuperAdmin && member.activo && member.id !== currentUser?.id && (
                        <button
                          onClick={() => {
                            if (confirm('¿Seguro que deseas desactivar este usuario?')) {
                              deactivateMutation.mutate(member.id);
                            }
                          }}
                          className="text-sm text-red-600 hover:text-red-700"
                          disabled={deactivateMutation.isPending}
                        >
                          Desactivar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateStaffModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          isLoading={createMutation.isPending}
          error={createMutation.error as Error | null}
        />
      )}

      {/* Edit Modal */}
      {selectedStaff && (
        <EditStaffModal
          staff={selectedStaff}
          onClose={() => setSelectedStaff(null)}
          onSubmit={(data) => updateMutation.mutate({ id: selectedStaff.id, data })}
          isLoading={updateMutation.isPending}
          error={updateMutation.error as Error | null}
        />
      )}
    </div>
  );
}

// Create Staff Modal
function CreateStaffModal({
  onClose,
  onSubmit,
  isLoading,
  error,
}: {
  onClose: () => void;
  onSubmit: (data: { email: string; password: string; nombre: string; role: string }) => void;
  isLoading: boolean;
  error: Error | null;
}) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nombre: '',
    role: 'READ_ONLY',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <h2 className="text-xl font-bold mb-4">Nuevo Staff</h2>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error.message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            {/* C12 R4 — htmlFor/id pair for a11y. */}
            <label htmlFor="create-staff-nombre" className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
            <input
              id="create-staff-nombre"
              type="text"
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              className="input"
              required
            />
          </div>

          <div>
            <label htmlFor="create-staff-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              id="create-staff-email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="input"
              required
            />
          </div>

          <div>
            <label htmlFor="create-staff-password" className="block text-sm font-medium text-gray-700 mb-1">Password (min 12 caracteres)</label>
            <input
              id="create-staff-password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="input"
              minLength={12}
              required
            />
          </div>

          <div>
            <label htmlFor="create-staff-role" className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
            <select
              id="create-staff-role"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="input"
            >
              {ROLES.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancelar
            </button>
            <button type="submit" className="btn-primary flex-1" disabled={isLoading}>
              {isLoading ? 'Creando...' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit Staff Modal
function EditStaffModal({
  staff,
  onClose,
  onSubmit,
  isLoading,
  error,
}: {
  staff: StaffMember;
  onClose: () => void;
  onSubmit: (data: { nombre?: string; role?: string; activo?: boolean }) => void;
  isLoading: boolean;
  error: Error | null;
}) {
  const [formData, setFormData] = useState({
    nombre: staff.nombre,
    role: staff.role,
    activo: staff.activo,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <h2 className="text-xl font-bold mb-4">Editar Staff</h2>
        <p className="text-sm text-gray-500 mb-4">{staff.email}</p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error.message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            {/* C12 R4 — htmlFor/id pair for a11y. */}
            <label htmlFor="edit-staff-nombre" className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
            <input
              id="edit-staff-nombre"
              type="text"
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              className="input"
              required
            />
          </div>

          <div>
            <label htmlFor="edit-staff-role" className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
            <select
              id="edit-staff-role"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="input"
            >
              {ROLES.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="activo"
              checked={formData.activo}
              onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
              className="rounded border-gray-300"
            />
            <label htmlFor="activo" className="text-sm text-gray-700">
              Usuario activo
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancelar
            </button>
            <button type="submit" className="btn-primary flex-1" disabled={isLoading}>
              {isLoading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
